import {
  ColorClass,
  Icon,
  IconIdentifier,
  IndentedTree,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isNil } from 'lodash';
import React from 'react';
import type { LayerNode } from 'shared/api';
import type { CesiumProxy } from 'shared/cesium';
import { formatNumberUS } from 'shared/helpers';
import { OverflowTextTooltip } from 'shared/components';

type DxfSublayerListProps = {
  nodes: LayerNode[];
  hiddenCadNodeIds: Set<string>;
  onToggleNode: (nodeId: string) => void;
  cesiumProxy: CesiumProxy | null;
};

export const DxfSublayerList: React.FC<DxfSublayerListProps> = ({
  nodes,
  hiddenCadNodeIds,
  onToggleNode,
  cesiumProxy,
}) => {
  // Handlers.
  const zoomToNode = (node: LayerNode) => {
    cesiumProxy?.flyToBounds(node.properties.bounds);
  };

  const getWarningTooltip = (node: LayerNode): string | null => {
    if (node.properties.unsupportedEntityCount === 0) {
      return null;
    }
    const supportedCount =
      node.properties.entityCount - node.properties.unsupportedEntityCount;

    if (supportedCount === 0) {
      return 'All entities are unsupported.';
    }
    const entityLabel =
      node.properties.unsupportedEntityCount === 1
        ? 'entity is'
        : 'entities are';

    return `${formatNumberUS(node.properties.unsupportedEntityCount)} ${entityLabel} not supported.`;
  };

  return (
    <IndentedTree visible={true} className="dxf-sublayer-list">
      {nodes.map((node) => {
        const isVisible = !hiddenCadNodeIds.has(node.id);
        const warningText = getWarningTooltip(node);

        return (
          <div
            key={node.id}
            className={classNames('dxf-sublayer-item', {
              'dxf-sublayer-item--hidden': !isVisible,
            })}
          >
            <OverflowTextTooltip
              className="dxf-sublayer-item__name"
              text={node.name}
              maxLength={35}
            />
            {!isNil(warningText) && (
              <Tooltip
                hoverText={warningText}
                placement={Placement.Top}
                className="dxf-sublayer-item__warning-tooltip"
              >
                <Icon
                  identifier={IconIdentifier.Warning}
                  size={14}
                  className="dxf-sublayer-item__warning-icon"
                />
              </Tooltip>
            )}
            <span className="dxf-sublayer-item__count">
              {node.properties.entityCount.toLocaleString('en-IN')}
            </span>

            <div className="dxf-sublayer-item__actions">
              <Icon
                className="dxf-sublayer-item__zoom-icon"
                identifier={IconIdentifier.GCPAvailableTagged}
                colorClass={ColorClass.Neutral250}
                size={14}
                isDisabled={!isVisible}
                onClick={(e: React.MouseEvent<SVGElement>) => {
                  e.stopPropagation();

                  if (isVisible) {
                    zoomToNode(node);
                  }
                }}
              />
              <Icon
                className="dxf-sublayer-item__visibility-icon"
                colorClass={ColorClass.Neutral250}
                identifier={
                  isVisible
                    ? IconIdentifier.VisibilityOn
                    : IconIdentifier.VisibilityOff
                }
                size={14}
                onClick={(e: React.MouseEvent<SVGElement>) => {
                  e.stopPropagation();
                  onToggleNode(node.id);
                }}
              />
            </div>
          </div>
        );
      })}
    </IndentedTree>
  );
};
